<script setup>
import { ref, watch } from 'vue';

const emit = defineEmits(['select']);

const props = defineProps({
    options: {
        type: Array,
        required: true,
    },
    selectedOptionName: {
        type: String,
        required: false,
        default: null,
    },
    size: {
        type: String,
        required: false,
        default: 'md',
    },
    responsive: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const optionClasses = {
    base: 'inline-flex w-full items-center justify-center gap-1.5 font-medium transition-colors whitespace-nowrap focus:outline-none',
    selected: 'bg-myflowLavender-light text-myflowPurple-900',
    notSelected: 'bg-white text-gray-600 focus:bg-gray-100 hover:bg-gray-100 hover:text-gray-900',
};

const sizeClasses = {
    xs: 'px-2 py-0.5 text-[10px]',
    sm: 'px-2.5 py-1 text-xs',
    md: 'px-3 py-1.5 text-xs',
};

const findOption = (name) =>
    props.options.find((option) => option.name === name);

const selectedOption = ref(
    props.selectedOptionName
        ? findOption(props.selectedOptionName)
        : props.options[0]
);

const select = (option) => {
    selectedOption.value = option;
    emit('select', option);
};

watch(
    () => props.selectedOptionName,
    (newVal) => {
        if (newVal === null) {
            return;
        }

        selectedOption.value = findOption(newVal) ?? selectedOption.value;
    }
);

watch(
    () => props.options,
    () => {
        const optionName =
            props.selectedOptionName ?? selectedOption.value?.name;

        selectedOption.value = optionName
            ? findOption(optionName) ?? props.options[0]
            : props.options[0];
    }
);
</script>

<template>
    <div class="w-full mb-5" :class="responsive ? '@container' : ''">
        <div
            class="flex w-full overflow-hidden rounded-md border bg-white border-myflowPurple-200"
            :class="
                responsive
                    ? 'flex-col divide-y divide-myflowPurple-200 @sm:flex-row @sm:divide-y-0 @sm:divide-x'
                    : 'flex-row divide-x divide-myflowPurple-200'
            "
        >
            <button
                v-for="(option, o) in props.options"
                v-bind:key="o"
                type="button"
                class="flex-1 basis-0"
                :aria-pressed="selectedOption?.name === option.name"
                :class="[
                    selectedOption?.name === option.name
                        ? optionClasses.selected
                        : optionClasses.notSelected,
                    optionClasses.base,
                    sizeClasses[props.size] ?? sizeClasses.md,
                ]"
                v-on:click="select(option)"
            >
                <i
                    v-if="option.icon"
                    :class="option.icon"
                    aria-hidden="true"
                ></i>
                <span>{{ option.label }}</span>
                <span
                    v-if="option.count !== undefined"
                    :class="
                        selectedOption?.name === option.name
                            ? 'text-gray-300'
                            : 'text-gray-400'
                    "
                >
                    ({{ option.count }})
                </span>
            </button>
        </div>
    </div>
</template>
